/**
 * ConfigStatusPill — "is this device actually running what we pushed?"
 *
 * This renders the applied-state verifier's verdict. The rule that governs every choice in
 * this file: A GREEN TICK MUST MEAN EVIDENCE. The verifier exists because the uCentral agent
 * reports config as `applied` while the hardware may still be running the old config (silent
 * divergence, root-caused on real hardware 2026-09-09). If the UI renders "we could not
 * check" the same way it renders "we checked and it matched", the defect is simply recreated
 * one layer up, and the feature is worse than useless because it is now trusted.
 *
 * Hence:
 *   - UNKNOWN is never on the healthy ramp. Never a tick. It reads as a state you can act on
 *     ("Not verifiable", "Checking") rather than as a pass.
 *   - VERIFIED is qualified by COVERAGE, always. "Up to date" is only said when the coverage
 *     is `change` — i.e. fields belonging to the thing the operator actually changed were
 *     observed and agreed. A whole-document match gets the weaker, honest "Matches pushed
 *     config", because it says nothing about any particular change.
 *   - DIVERGED is loud. It is the signal the whole feature was built to surface.
 *   - A verdict on an OFFLINE device (server flag `stale`) is history, not state: neutral and
 *     aged ("Last confirmed 1d ago (device offline)"), never a live green tick (OP 2026-10-06).
 */
import type { ConfigVerdict, ConfigCoverage, DeviceConfigStatus } from "@/types/domain";
import { staleConfigLabel, type ConfigStaleness } from "@/lib/liveStatus";
import { StatusPill } from "./StatusPill";

/**
 * Reason codes from services/config_verify, mapped to sentences an operator can act on.
 *
 * Every one of these is an UNKNOWN sub-state, and each says something different about what to
 * do next — "we pushed it seconds ago, wait" is a very different instruction from "this
 * device's config has nothing we know how to observe". Flattening them to one "Unknown" chip
 * would throw away the only actionable part of the verdict.
 */
const REASON_TEXT: Record<string, string> = {
  never_verified: "No check has run yet.",
  /* Inside one statistics interval (+ margin) of a push: not a lag yet, just the schedule. */
  awaiting_first_report: "Pushed — waiting for the device's next scheduled report.",
  pending_apply: "Pushed — the device has not re-reported yet.",
  /* services/config_verify REASON_PENDING_DELIVERY: owgw has not handed the config to the device
     yet (command still pending, e.g. the device was unreachable). Grace starts at delivery. */
  pending_delivery: "Queued — the gateway has not delivered this config to the device yet.",
  /* services/config_verify REASON_NOT_UCENTRAL_MANAGED: owgw holds only a {uuid} placeholder.
     ECS/RSW switches are configured over the CLI relay, never by a uCentral push (2026-10-11). */
  not_ucentral_managed:
    "Configured by Meridian over the switch CLI, not uCentral — config generation not tracked.",
  config_generation_ahead: "A newer config was pushed after this one.",
  stale_state: "The device's last report predates the push.",
  no_config_generation: "The device reports no config generation, so evidence cannot be dated.",
  no_device_state: "The device has reported no state to compare against.",
  owgw_unavailable: "The gateway was unreachable, so nothing could be read back.",
  no_observable_fields: "This config has no fields that appear in device state.",
  change_not_observable: "The changed settings do not appear in device state.",
  change_not_observed: "The changed settings were not present in the device's report.",
  field_mismatch: "The device is running something other than what we pushed.",
  config_generation_stale: "The device is still running an older config generation.",
  /* services/config_verify REASON_DEVICE_REJECTED: the device answered the push with a uCentral
     rejection; owgw holds the refused config, the device runs its previous one (2026-10-09). */
  device_rejected: "The device rejected the pushed config and is still running its previous one.",
  /* services/config_verify REASON_STALE_UPGRADE (OP#1814): the gateway's own reconnect upgrade
     re-sent an OLDER config after the device applied the intended one. */
  reverted_by_reconnect:
    "The gateway re-sent an older config when the device reconnected; the intended config is not on the device.",
  verified: "Device-reported state matched what we pushed.",
};

interface Rendered {
  ramp: "healthy" | "warn" | "critical" | "unknown";
  label: string;
  hollow?: boolean;
}

/** Verdict + coverage -> how it renders. Exported so the list column and the device page
 *  identity rail cannot drift apart on what a verdict means.
 *
 *  `staleness` (optional, from the verification endpoints): when the device is not online the
 *  verdict is a historical last word, so it renders NEUTRAL with its age ("Last confirmed 1d
 *  ago (device offline)") instead of on the healthy/critical ramp, which is reserved for live
 *  operational state. The verdict is not rewritten -- see lib/liveStatus. */
export function renderConfigStatus(
  verdict: ConfigVerdict | undefined,
  coverage: ConfigCoverage | null | undefined,
  staleness?: ConfigStaleness,
): Rendered {
  const staleLabel = staleConfigLabel(verdict, staleness);
  if (staleLabel) {
    return { ramp: "unknown", label: staleLabel, hollow: true };
  }
  if (verdict === "diverged") {
    return { ramp: "critical", label: "Diverged" };
  }
  if (verdict === "verified") {
    // Coverage is load-bearing, not a nicety. `change` means the operator's change was seen
    // on the device; `document` means only that the device matches the document owgw holds.
    return coverage === "change"
      ? { ramp: "healthy", label: "Up to date" }
      : { ramp: "healthy", label: "Matches pushed config" };
  }
  // Everything else is UNKNOWN — including an absent verdict, which is what a device missing
  // from the fleet response looks like. Neutral and hollow: we have no evidence.
  return { ramp: "unknown", label: "Not verified", hollow: true };
}

export function ConfigStatusPill({
  status,
  className,
}: {
  /** Undefined => no row for this device at all, which renders as unknown, never as a pass. */
  status?: DeviceConfigStatus;
  className?: string;
}) {
  const r = renderConfigStatus(status?.verdict, status?.coverage, status);

  // The tooltip carries the WHY. A one-word chip is scannable; the reason is what an operator
  // needs the moment the chip is not green, and it costs nothing to attach.
  const reason = status?.reason ? REASON_TEXT[status.reason] ?? status.reason : undefined;
  const mismatches =
    status && status.mismatch_count > 0
      ? `${status.mismatch_count} field${status.mismatch_count === 1 ? "" : "s"} differ`
      : undefined;
  // An offline device's verdict is not live: say so in the tooltip too (the stale label
  // already says it for verified/diverged; this covers "Not verified" on an offline device).
  const offline = status?.stale ? "Device offline: this is its last known state, not live." : undefined;
  // Config-incompatible (services/config_compat): the verdict stays honest (e.g. "Rejected by
  // device"), and the tooltip says the device is left out of site operations, and why -- so a
  // red chip on an AP that needs a reflash is not read as a push that keeps failing.
  const excluded = status?.config_excluded
    ? `Excluded from site operations (not pushed, not counted as a failure): ${
        status.config_excluded_reason ?? "not under Meridian config"
      }.`
    : undefined;
  const title = [offline, reason, mismatches, excluded].filter(Boolean).join(" ");

  return (
    <StatusPill status={r.ramp} className={className} hollow={r.hollow} title={title || undefined}>
      {/* A device REJECTION is a diverged verdict with a definite cause: name it on the chip,
          so it is not read as an ordinary drift. Stale (offline) rows keep their aged label. */}
      {status?.verdict === "diverged" && status.reason === "device_rejected" && !status.stale
        ? "Rejected by device"
        : status?.verdict === "diverged" && status.reason === "reverted_by_reconnect" && !status.stale
          ? "Reverted by gateway"
          : /* A CLI-relay switch is not "unverified" in a fixable sense: there is no uCentral
             config to verify. Name it, so it is not read as a pending/failed check. */
          status?.verdict === "unknown" && status.reason === "not_ucentral_managed"
          ? "CLI-managed"
          : r.label}
      {status && status.mismatch_count > 0 ? (
        <span className="font-mono tabular-nums">({status.mismatch_count})</span>
      ) : null}
    </StatusPill>
  );
}

/** The human sentence behind a reason code, for the device page (which has room for it). */
export function configReasonText(reason: string | undefined | null): string | null {
  if (!reason) return null;
  return REASON_TEXT[reason] ?? reason;
}

/**
 * "Config incompatible" badge: the device is EXCLUDED from Meridian config (services/
 * config_compat) -- stock firmware that refuses every config, a streak of wholesale
 * rejections, or an operator's "Exclude from Meridian config". Neutral, never red: it is a
 * state to act on (reflash / include again), not a failing push. Renders nothing otherwise.
 */
export function ConfigIncompatibleBadge({
  excluded,
  label,
  reason,
  className,
}: {
  excluded?: boolean;
  label?: string | null;
  reason?: string | null;
  className?: string;
}) {
  if (!excluded) return null;
  return (
    <span
      className={`inline-flex items-center rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-ink-muted ${className ?? ""}`}
      title={[label, reason].filter(Boolean).join(" -- ") || undefined}
    >
      Config incompatible
    </span>
  );
}
